/**
 * Google Maps JavaScript API bootstrap for Landoo.
 *
 * The API key and Map ID come from environment variables only — they are never
 * hard-coded, logged, or rendered. Error messages describe the failure class
 * without ever echoing the key value.
 *
 * Browser-only: import from client components / lazy chunks.
 */

import { importLibrary, setOptions } from "@googlemaps/js-api-loader";

/**
 * Browser key: the project's own key first, else the Lovable Google Maps
 * connector's managed browser key. Never hard-coded.
 */
export const GOOGLE_MAPS_API_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY ||
  import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_MAPS_BROWSER_KEY) as string | undefined;
/**
 * Map ID is required by AdvancedMarkerElement. When the project has not set a
 * production Map ID we fall back to Google's DEMO_MAP_ID so the map still
 * renders (styling is generic and Google may watermark it).
 */
export const GOOGLE_MAP_ID = ((import.meta.env.VITE_GOOGLE_MAP_ID as string | undefined) ||
  "DEMO_MAP_ID") as string;
export const HAS_CUSTOM_MAP_ID = Boolean(import.meta.env.VITE_GOOGLE_MAP_ID);

/** Default view: Madurai, Tamil Nadu — fallback only, never overwrites a GPS fix. */
export const MADURAI = { lat: 9.9252, lng: 78.1198 };
export const DEFAULT_ZOOM = 12;
export const MAP_MIN_ZOOM = 4;
export const MAP_MAX_ZOOM = 20;

export type GoogleMapsLibs = {
  maps: google.maps.MapsLibrary;
  marker: google.maps.MarkerLibrary;
};

export class GoogleMapsConfigError extends Error {}
/** Raised when Google rejects the key for this domain (RefererNotAllowedMapError). */
export class GoogleMapsAuthError extends Error {}

let cached: Promise<GoogleMapsLibs> | null = null;
let scriptSeq = 0;

type AuthFailureListener = (err: GoogleMapsAuthError) => void;
const authListeners = new Set<AuthFailureListener>();

/**
 * Google reports key/referrer rejection through the global `gm_authFailure`
 * callback, not through a rejected promise — so we bridge it into our own
 * listener set and let the UI show the friendly fallback.
 */
function installAuthFailureHook() {
  if (typeof window === "undefined") return;
  const w = window as unknown as { gm_authFailure?: () => void; __landooGmHook?: boolean };
  if (w.__landooGmHook) return;
  w.__landooGmHook = true;
  w.gm_authFailure = () => {
    cached = null;
    const err = new GoogleMapsAuthError("RefererNotAllowedMapError");
    authListeners.forEach((fn) => fn(err));
  };
}

/** Subscribe to Google Maps auth failures. Returns an unsubscribe function. */
export function onGoogleMapsAuthFailure(fn: AuthFailureListener): () => void {
  installAuthFailureHook();
  authListeners.add(fn);
  return () => authListeners.delete(fn);
}

/** Drops the cached loader so the next `loadGoogleMaps()` retries from scratch. */
export function resetGoogleMaps() {
  cached = null;
  scriptSeq += 1;
}

/**
 * Loads the `maps` and `marker` libraries exactly once per session. Places is
 * loaded separately (and lazily) so the first paint of the map stays fast.
 */
export function loadGoogleMaps(): Promise<GoogleMapsLibs> {
  if (cached) return cached;

  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(
      new GoogleMapsConfigError(
        "Google Maps API key is not configured. Add VITE_GOOGLE_MAPS_API_KEY to the project environment.",
      ),
    );
  }

  installAuthFailureHook();
  setOptions({ key: GOOGLE_MAPS_API_KEY, v: "weekly", region: "IN", mapIds: [GOOGLE_MAP_ID] });

  const seq = scriptSeq;
  cached = Promise.all([importLibrary("maps"), importLibrary("marker")])
    .then(([maps, marker]) => ({ maps, marker }) as GoogleMapsLibs)
    .catch((err: unknown) => {
      if (seq === scriptSeq) cached = null;
      throw new Error(describeMapsError(err));
    });

  return cached;
}

/** Lazily loads the Places library — only when the user actually searches. */
export function loadPlaces(): Promise<google.maps.PlacesLibrary> {
  return loadGoogleMaps().then(() => importLibrary("places") as Promise<google.maps.PlacesLibrary>);
}

/** True when the message describes a domain/referrer rejection. */
export function isReferrerError(err: unknown): boolean {
  if (err instanceof GoogleMapsAuthError) return true;
  const text = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
  return text.includes("referrernotallowed") || text.includes("referer");
}

/**
 * Turns a Google Maps failure into an actionable message.
 * Never includes the API key.
 */
export function describeMapsError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? "");
  const text = raw.toLowerCase();

  if (err instanceof GoogleMapsConfigError) return raw;
  if (isReferrerError(err)) {
    return `Google Maps is not authorised for this domain (${
      typeof window !== "undefined" ? window.location.hostname : "this site"
    }). Add it to the API key's HTTP referrer allowlist in Google Cloud Console.`;
  }
  if (text.includes("billingnotenabled") || text.includes("billing")) {
    return "Google Maps rejected the request because billing is not enabled on the Google Cloud project.";
  }
  if (text.includes("apinotactivated") || text.includes("not activated")) {
    return "The Maps JavaScript API (or Places API) is not enabled on the Google Cloud project.";
  }
  if (text.includes("invalidkey") || text.includes("request_denied")) {
    return "Google Maps denied the request. Check the API key restrictions and enabled APIs in Google Cloud Console.";
  }
  return "Google Maps failed to load. Check the network connection and the Google Cloud API configuration.";
}


/** Valid, plottable coordinates — rejects missing, NaN, out-of-range and null island. */
export function isPlottable(lat: unknown, lng: unknown): lat is number {
  if (typeof lat !== "number" || typeof lng !== "number") return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  if (lat === 0 && lng === 0) return false;
  return true;
}

/** Safe Google Maps directions deep link (handles both app and web). */
export function directionsUrl(
  destination: { lat: number; lng: number },
  origin?: { lat: number; lng: number } | null,
): string {
  const params = new URLSearchParams({
    api: "1",
    destination: `${destination.lat},${destination.lng}`,
    travelmode: "driving",
  });
  if (origin && isPlottable(origin.lat, origin.lng)) {
    params.set("origin", `${origin.lat},${origin.lng}`);
  }
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}
